import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { OrganizationalRecordLinkedDocumentDocument, OrganizationalRecordLinkedEntryDocument, OrganizationalRecordDocumentContentDocument,
  OrganizationalRecordItemKind } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { base64ToBlob } from "@/lib/use-open-document";
import { downloadBlob } from "@/lib/file-download";
import { OrganizationalRecordBacklinks, OrganizationalRecordLinkList, type OrganizationalRecordReference, type OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";
import { OrganizationalRecordComments } from "./OrganizationalRecordComments";

const previewTypes = new Set(["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp", "text/plain"]);

export function OrganizationalRecordItemDialog({ recordId, item, canWrite, onClose, onNavigate }: {
  recordId: string; item: OrganizationalRecordReference; canWrite: boolean; onClose: () => void; onNavigate: OrganizationalRecordNavigate;
}) {
  return <Dialog open onClose={onClose} maxWidth="lg" title={<FormattedMessage id={item.kind === OrganizationalRecordItemKind.Comment ? "organizationalRecord.links.comment" : "organizationalRecord.links.details"} />}>
    {item.kind === OrganizationalRecordItemKind.Document ? <DocumentDetail recordId={recordId} item={item} onNavigate={onNavigate} />
      : item.kind === OrganizationalRecordItemKind.Entry ? <EntryDetail recordId={recordId} item={item} canWrite={canWrite} onNavigate={onNavigate} />
      : <OrganizationalRecordComments recordId={recordId} entryId={item.entryId ?? undefined} focusId={item.id} canWrite={canWrite} onNavigate={onNavigate} />}
    <div className="mt-5 flex justify-end"><Button variant="secondary" onClick={onClose}><FormattedMessage id="common.close" /></Button></div>
  </Dialog>;
}

function EntryDetail({ recordId, item, canWrite, onNavigate }: { recordId: string; item: OrganizationalRecordReference; canWrite: boolean; onNavigate: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  const query = useQuery(OrganizationalRecordLinkedEntryDocument, { variables: { id: item.id }, fetchPolicy: "no-cache" });
  const entry = query.data?.organizationalRecordEntry;
  if (query.loading) return <p><FormattedMessage id="common.loading" /></p>;
  if (query.error) return <p role="alert">{userErrorMessage(intl, query.error)}</p>;
  if (!entry || entry.recordId !== recordId) return <p role="alert"><FormattedMessage id="organizationalRecord.links.unavailable" /></p>;
  return <div className="space-y-4">
    <h3 className="smycles-field-value font-medium break-words">{entry.title}</h3>
    <p className="smycles-field-value whitespace-pre-wrap break-words">{entry.content}</p>
    <OrganizationalRecordPerson person={entry.createdBy} />
    <OrganizationalRecordLinkList links={entry.links} onNavigate={onNavigate} />
    <OrganizationalRecordBacklinks recordId={recordId} target={item} onNavigate={onNavigate} />
    <OrganizationalRecordComments recordId={recordId} entryId={item.id} canWrite={canWrite} onNavigate={onNavigate} />
  </div>;
}

function DocumentDetail({ recordId, item, onNavigate }: { recordId: string; item: OrganizationalRecordReference; onNavigate: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  const query = useQuery(OrganizationalRecordLinkedDocumentDocument, { variables: { id: item.id }, fetchPolicy: "no-cache" });
  const document = query.data?.organizationalRecordDocument;
  if (query.loading) return <p><FormattedMessage id="common.loading" /></p>;
  if (query.error) return <p role="alert">{userErrorMessage(intl, query.error)}</p>;
  if (!document || document.recordId !== recordId) return <p role="alert"><FormattedMessage id="organizationalRecord.links.unavailable" /></p>;
  return <div className="space-y-4">
    <h3 className="smycles-field-value font-medium break-words">{document.title}</h3>
    <DocumentFile id={document.id} fileName={document.fileName} title={document.title} />
    <OrganizationalRecordBacklinks recordId={recordId} target={item} onNavigate={onNavigate} />
  </div>;
}

function DocumentFile({ id, fileName, title }: { id: string; fileName: string; title: string }) {
  const intl = useIntl();
  const client = useApolloClient();
  const [state, setState] = useState<{ url?: string; blob?: Blob; error?: unknown }>({});
  useEffect(() => {
    let active = true;
    let url: string | undefined;
    void client.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" }).then(({ data }) => {
      if (!active) return;
      const file = data?.organizationalRecordDocument?.content;
      if (!file?.content) throw new Error("Missing document bytes");
      const mime = file.mimeType ?? "application/octet-stream";
      const blob = base64ToBlob(file.content, mime);
      if (previewTypes.has(mime)) url = URL.createObjectURL(blob);
      else downloadBlob(blob, fileName);
      setState({ url, blob });
    }).catch(error => { if (active) setState({ error }); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [client, id, fileName]);
  return <>
    {state.error ? <p role="alert">{userErrorMessage(intl, state.error)}</p>
      : !state.blob ? <p><FormattedMessage id="common.loading" /></p>
      : state.url ? <iframe title={title} src={state.url} sandbox="" className="h-[45vh] w-full border-0" />
      : <p className="smycles-field-meta"><FormattedMessage id="organizationalRecord.links.downloaded" /></p>}
    {state.blob && <Button variant="secondary" size="sm" onClick={() => downloadBlob(state.blob!, fileName)}><FormattedMessage id="organizationalRecord.download" /></Button>}
  </>;
}
import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
